import React, { useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Select, { components } from "react-select";
import {
  FiArrowLeft,
  FiGlobe,
} from "react-icons/fi";
import { IoIosSearch } from "react-icons/io";
import "./AssistantSettingsPanel.scss";

const LANGUAGE_OPTIONS = [
  "English",
  "Spanish",
  "French",
  "German",
  "Italian",
  "Dutch",
  "Portuguese",
  "Norwegian",
  "Swedish",
  "Danish",
  "Welsh",
  "Arabic",
  "Turkish",
  "Chinese (Simplified & Traditional)",
  "Japanese",
  "Korean",
  "Thai",
  "Hindi",
  "Bengali",
  "Tamil",
  "Telugu",
  "Marathi",
  "Gujarati",
  "Kannada",
  "Malayalam",
  "Punjabi",
  "Urdu",
  "Odia",
  "Assamese",
  "Meiteilon (Manipuri)",
];

const SORTED_LANGUAGE_OPTIONS = [
  "English",
  ...LANGUAGE_OPTIONS.filter((option) => option !== "English").sort((a, b) =>
    a.localeCompare(b),
  ),
];

const SearchMenu = (props) => {
  const selectProps = props.selectProps || {};
  const searchValue = selectProps.searchValue || "";
  const setSearchValue = selectProps.setSearchValue || (() => {});
  const searchPlaceholder = selectProps.searchPlaceholder || "Search";
  const onSearchInteractionStart = selectProps.onSearchInteractionStart;
  const showSearch = selectProps.showSearch !== false;
  const searchInputRef = React.useRef(null);

  const handleSearchMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onSearchInteractionStart?.();
    searchInputRef.current?.focus();
  };

  return (
    <components.Menu {...props}>
      {showSearch ? (
        <div
          className="assistant-settings-selectbox__search"
          onMouseDown={handleSearchMouseDown}
        >
          <IoIosSearch aria-hidden="true" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchValue}
            placeholder={searchPlaceholder}
            onChange={(event) => setSearchValue(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
            onMouseDown={handleSearchMouseDown}
            aria-label={searchPlaceholder}
          />
        </div>
      ) : null}
      {props.children}
    </components.Menu>
  );
};

SearchMenu.propTypes = {
  children: PropTypes.node,
  selectProps: PropTypes.shape({
    searchValue: PropTypes.string,
    setSearchValue: PropTypes.func,
    searchPlaceholder: PropTypes.string,
    onSearchInteractionStart: PropTypes.func,
    showSearch: PropTypes.bool,
  }).isRequired,
};

const getSelectStyles = ({ hasIcon }) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    height: 40,
    borderRadius: 5,
    borderColor: state.isFocused
      ? "#bda8ff"
      : "#d9dee8",
    backgroundColor: "#ffffff",
    boxShadow: state.isFocused
      ? `0 0 0 1px ${"#dccfff"}`
      : "none",
    paddingLeft: hasIcon ? 30 : 0,
    cursor: "pointer",
    transition: "border-color 0.18s ease, box-shadow 0.18s ease",
    "&:hover": {
      borderColor: state.isFocused
        ? "#bda8ff"
        : "#cfd7e6",
      backgroundColor: "#fafbff",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
    height: 38,
  }),
  singleValue: (base) => ({
    ...base,
    color: "#1a1d24",
    fontSize: 13,
    fontWeight: 500,
    marginLeft: 0,
    marginRight: 0,
  }),
  input: (base) => ({
    ...base,
    color: "#1a1d24",
    margin: 0,
    padding: 0,
  }),
  placeholder: (base) => ({
    ...base,
    color: "#8b93a6",
    fontSize: 13,
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: "#67728a",
    padding: "0 10px 0 8px",
    transition: "color 0.18s ease, transform 0.18s ease",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
    "&:hover": {
      color: "#1a1d24",
    },
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 5000,
  }),
  menu: (base) => ({
    ...base,
    marginTop: 6,
    borderRadius: 5,
    overflow: "hidden",
    border: `1px solid ${"#d9dee8"}`,
    boxShadow: "0 12px 24px rgba(15, 23, 42, 0.12)",
    backgroundColor: "#ffffff",
  }),
  menuList: (base) => ({
    ...base,
    padding: 4,
    maxHeight: 220,
  }),
  option: (base, state) => ({
    ...base,
    borderRadius: 5,
    padding: "9px 10px",
    fontSize: 13,
    fontWeight: state.isSelected ? 600 : 500,
    color: state.isSelected
      ? "#6f52d9"
      : "#1a1d24",
    backgroundColor: state.isSelected
      ? "#f7f3ff"
      : state.isFocused
        ? "#f6f8fb"
        : "transparent",
    cursor: "pointer",
    "&:active": {
      backgroundColor: "#f0ebff",
    },
  }),
});

const SelectField = ({
  icon: Icon,
  value,
  options,
  onChange,
  ariaLabel,
  showSearch = true,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const ignoreCloseRef = useRef(false);
  const selectOptions = options.map((option) =>
    typeof option === "string" ? { value: option, label: option } : option,
  );
  const selectedOption =
    selectOptions.find((option) => option.value === value) ?? null;
  const filteredOptions = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    if (!showSearch || !normalizedSearch) {
      return selectOptions;
    }

    return selectOptions.filter((option) =>
      option.label.toLowerCase().includes(normalizedSearch),
    );
  }, [searchValue, selectOptions, showSearch]);

  const handleSearchInteractionStart = () => {
    ignoreCloseRef.current = true;
    setMenuIsOpen(true);
  };

  return (
    <div className="assistant-settings-select">
      {Icon ? (
        <span className="assistant-settings-select__icon" aria-hidden="true">
          <Icon />
        </span>
      ) : null}
      <Select
        inputId={ariaLabel}
        aria-label={ariaLabel}
        classNamePrefix="assistant-settings-selectbox"
        value={selectedOption}
        options={filteredOptions}
        isSearchable={false}
        menuIsOpen={menuIsOpen}
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPosition="fixed"
        closeMenuOnSelect
        components={{ Menu: SearchMenu }}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        searchPlaceholder={`Search ${ariaLabel.toLowerCase()}`}
        showSearch={showSearch}
        onSearchInteractionStart={handleSearchInteractionStart}
        styles={getSelectStyles({ hasIcon: Boolean(Icon) })}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => {
          if (ignoreCloseRef.current) {
            ignoreCloseRef.current = false;
            return;
          }
          setMenuIsOpen(false);
          setSearchValue("");
        }}
        onChange={(selectedOption) => {
          onChange(selectedOption?.value ?? "");
          setMenuIsOpen(false);
          setSearchValue("");
        }}
      />
    </div>
  );
};

SelectField.propTypes = {
  icon: PropTypes.elementType,
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      }),
    ]),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  showSearch: PropTypes.bool,
};

const SettingsViewFrame = ({ title, onBack, children }) => (
  <div className="assistant-settings-panel">
    <div className="assistant-settings-panel__header">
      <button
        type="button"
        className="assistant-settings-panel__back"
        onClick={onBack}
        aria-label="Back to chat"
      >
        <FiArrowLeft />
      </button>
      <div className="assistant-settings-panel__header-copy">
        <h2>{title}</h2>
      </div>
    </div>
    <div className="assistant-settings-panel__body">{children}</div>
  </div>
);

SettingsViewFrame.propTypes = {
  title: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

const AssistantSettingsPanel = ({
  preferencesDraft,
  onPreferencesChange,
  onClose,
}) => (
  <SettingsViewFrame title="Settings" onBack={onClose}>
    <div className="assistant-settings-card assistant-settings-card--split">
      <div className="assistant-settings-field-copy">
        <h3>Language</h3>
        <p>Choose the language Lou will use to respond.</p>
      </div>
      <SelectField
        icon={FiGlobe}
        value={preferencesDraft.language}
        options={SORTED_LANGUAGE_OPTIONS}
        onChange={(nextValue) =>
          onPreferencesChange((current) => ({
            ...current,
            language: nextValue,
          }))
        }
        ariaLabel="Language"
      />
    </div>
    <p className="assistant-settings-custom-note assistant-settings-custom-note--preferences">
      <span className="assistant-settings-custom-note__label">Note:</span>
      <span>
        Language changes are applied when you start a new conversation.
      </span>
    </p>
  </SettingsViewFrame>
);

AssistantSettingsPanel.propTypes = {
  preferencesDraft: PropTypes.shape({
    language: PropTypes.string.isRequired,
  }).isRequired,
  onPreferencesChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AssistantSettingsPanel;
